import { FunctionComponent, PropsWithChildren, useState } from 'react';
import { CardActions, CardContent, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
    usePostProductConfigurationMutation,
    usePutProductConfigurationMutation,
} from '@/api/v2/productConfigurations';
import { useNotification } from '@starjack/features/notifications';
import { useAuthentication } from '@/hooks/useAuthentication';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import { useGeneralProductConfigurationContext } from './ProductConfigurationGeneralConfiguration';

type SectionCardProps = {
    title: string;
    hideActions?: boolean;
};

export const SectionCard: FunctionComponent<
    PropsWithChildren<SectionCardProps>
> = ({ title, hideActions = false, children }) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { productConfiguration } = useGeneralProductConfigurationContext();
    const [postProductConfiguration, { isLoading: postLoading }] =
        usePostProductConfigurationMutation();
    const [putProductConfiguration, { isLoading: putLoading }] =
        usePutProductConfigurationMutation();
    const [expanded, setExpanded] = useState(false);
    const { addNotification } = useNotification();

    const onClick = () => {
        const action = productConfiguration.id
            ? putProductConfiguration
            : postProductConfiguration;

        action({ liftOperatorId, productConfiguration })
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Konfiguration erfolgreich gespeichert',
                    type: 'success',
                });
            })
            .catch(() => {
                addNotification({
                    message:
                        'Fehler beim Speichern. Bitte versuchen Sie es später noch einmal',
                    type: 'error',
                });
            });
    };

    const isLoading = postLoading || putLoading;
    return (
        <Accordion
            elevation={2}
            expanded={expanded}
            onChange={() => {
                setExpanded((prevState) => !prevState);
            }}
            sx={(theme) => ({
                borderRadius: theme.spacing(2),

                '&:before': {
                    display: 'none',
                },
            })}
        >
            <AccordionSummary
                expandIcon={
                    <ExpandMoreIcon
                        fontSize='medium'
                        sx={{
                            color: '#fff',
                        }}
                    />
                }
                sx={(theme) => ({
                    bgcolor: 'primary.main',
                    borderRadius: theme.spacing(2),
                    p: 4,
                    color: '#fff',
                    transition: theme.transitions.create(['background-color']),

                    '&:hover': {
                        bgcolor: 'primary.dark',
                    },

                    ...(expanded && {
                        borderBottomRightRadius: 0,
                        borderBottomLeftRadius: 0,
                    }),
                })}
            >
                <Typography variant='h6'>{title}</Typography>
            </AccordionSummary>

            <AccordionDetails>
                <CardContent>
                    <Stack spacing={5}>{children}</Stack>
                </CardContent>
                {!hideActions && (
                    <CardActions
                        sx={{
                            p: 4,
                        }}
                    >
                        <LoadingButton
                            loading={isLoading}
                            onClick={onClick}
                            variant='contained'
                        >
                            Speichern
                        </LoadingButton>
                    </CardActions>
                )}
            </AccordionDetails>
        </Accordion>
    );
};
